import type {
  Lead,
  LeadEventWithStatusDto,
} from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@up2stay/up2promo-ui';
import { CalendarDays, CalendarPlus, Loader2, Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { CreateLeadEventDialog } from '@/core/components/leads/create-lead-event-dialog';
import { LEAD_DETAIL_SECTION_TITLE_CLASS } from '@/core/components/leads/lead-detail/lead-detail-section-card';
import {
  buildEventNameMap,
  eventNamesFromLead,
  eventRefId,
  resolveEventDisplayName,
} from '@/core/lib/lead-event-names';

export type LeadDetailEventsLabels = {
  title: string;
  empty: string;
  add: string;
  remove: string;
  noEventsAvailable: string;
  updating: string;
  create: string;
  readOnlyHint?: string;
};

type LeadDetailEventsProps = {
  lead: Lead;
  events: LeadEventWithStatusDto[];
  editable: boolean;
  /** When true, the add menu includes "create event" (requires EVENTS_MANAGE). */
  canCreate: boolean;
  workspaceId: string | undefined;
  isUpdating: boolean;
  labels: LeadDetailEventsLabels;
  onAdd: (eventId: string) => void;
  onRemove: (eventId: string) => void;
  embedded?: boolean;
};

export function LeadDetailEvents({
  lead,
  events,
  editable,
  canCreate,
  workspaceId,
  isUpdating,
  labels,
  onAdd,
  onRemove,
  embedded = false,
}: LeadDetailEventsProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const showCreate = Boolean(editable && canCreate && workspaceId);

  const linkedIdList = useMemo(
    () => [
      ...new Set((lead.eventIds ?? []).map((id) => id.trim()).filter(Boolean)),
    ],
    [lead.eventIds],
  );

  const linkedIdLookup = useMemo(() => {
    const set = new Set<string>();
    for (const id of linkedIdList) {
      set.add(id);
      set.add(id.toLowerCase());
    }
    return set;
  }, [linkedIdList]);

  const eventById = useMemo(() => {
    const map = new Map<string, LeadEventWithStatusDto>();
    for (const ev of events) {
      const id = eventRefId(ev);
      if (!id) continue;
      map.set(id, ev);
      map.set(id.toLowerCase(), ev);
    }
    return map;
  }, [events]);

  const eventNameById = useMemo(
    () => buildEventNameMap(events, eventNamesFromLead(lead)),
    [events, lead],
  );

  const linkedEvents = useMemo(() => {
    const rows: { id: string; name: string }[] = [];
    for (const id of linkedIdList) {
      const ev = eventById.get(id) ?? eventById.get(id.toLowerCase());
      rows.push({
        id,
        name: resolveEventDisplayName(id, eventNameById, {
          storedName: ev?.name,
        }),
      });
    }
    rows.sort((a, b) => a.name.localeCompare(b.name));
    return rows;
  }, [linkedIdList, eventById, eventNameById]);

  const addableEvents = useMemo(
    () =>
      events
        .filter((ev) => {
          const id = eventRefId(ev);
          if (!id) return false;
          return (
            !linkedIdLookup.has(id) && !linkedIdLookup.has(id.toLowerCase())
          );
        })
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [events, linkedIdLookup],
  );

  return (
    <section
      className={embedded ? 'flex flex-col gap-1.5' : 'flex flex-col gap-3'}
    >
      <div className="flex items-center justify-between gap-2">
        <h3
          className={
            embedded
              ? 'text-[12px] font-normal text-muted-foreground'
              : LEAD_DETAIL_SECTION_TITLE_CLASS
          }
        >
          {labels.title}
        </h3>
        {editable ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {embedded ? (
                <button
                  type="button"
                  aria-label={labels.add}
                  disabled={
                    isUpdating || (addableEvents.length === 0 && !showCreate)
                  }
                  className="inline-flex h-5 items-center gap-1 rounded-md bg-muted px-1.5 text-[11px] text-muted-foreground"
                >
                  {isUpdating ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <Plus className="size-3" />
                  )}
                </button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={
                    isUpdating || (addableEvents.length === 0 && !showCreate)
                  }
                >
                  {isUpdating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-2 h-4 w-4" />
                  )}
                  {labels.add}
                </Button>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="max-h-64 overflow-y-auto"
            >
              {addableEvents.length === 0 && !showCreate ? (
                <DropdownMenuItem disabled>
                  {labels.noEventsAvailable}
                </DropdownMenuItem>
              ) : (
                addableEvents.map((ev) => (
                  <DropdownMenuItem
                    key={eventRefId(ev)}
                    onSelect={() => onAdd(eventRefId(ev))}
                  >
                    {ev.name}
                  </DropdownMenuItem>
                ))
              )}
              {showCreate ? (
                <>
                  {addableEvents.length > 0 ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuItem onSelect={() => setCreateOpen(true)}>
                    <CalendarPlus className="h-4 w-4" />
                    {labels.create}
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : labels.readOnlyHint ? (
          <p className="max-w-[16rem] text-right text-xs text-muted-foreground">
            {labels.readOnlyHint}
          </p>
        ) : null}
      </div>

      {linkedEvents.length === 0 ? (
        embedded ? null : (
          <p className="text-sm text-muted-foreground">{labels.empty}</p>
        )
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {linkedEvents.map((ev) => (
            <Badge
              key={ev.id}
              variant="secondary"
              className={
                embedded
                  ? 'inline-flex h-5 max-w-full gap-1 truncate border-transparent bg-muted px-1.5 text-[11px] font-normal'
                  : 'max-w-full gap-1 truncate border-transparent font-normal'
              }
              title={ev.name}
            >
              <CalendarDays className="h-3 w-3 shrink-0 opacity-70" />
              <span className="truncate">{ev.name}</span>
              {editable ? (
                <button
                  type="button"
                  className="ml-0.5 rounded-sm p-0.5 hover:bg-muted"
                  aria-label={labels.remove}
                  disabled={isUpdating}
                  onClick={() => onRemove(ev.id)}
                >
                  <X className="h-3 w-3 opacity-70" />
                </button>
              ) : null}
            </Badge>
          ))}
        </div>
      )}

      {showCreate && workspaceId ? (
        <CreateLeadEventDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          workspaceId={workspaceId}
          onCreated={(eventId) => {
            onAdd(eventId);
          }}
        />
      ) : null}
    </section>
  );
}
